import { useEffect, useState } from 'react';
import { getSocialBrandAssets } from '../../../util/api';
import { BRAND_ASSETS, mergeBrandAssets } from './shared/brandAssets';

/**
 * useBrandAssets — the ONE consumption seam the design editor uses to get the full
 * "Grafik" list (curated seed + the manager's self-serve uploads).
 *
 * The uploaded half is fetched ONCE and held in a module-level cache shared by every
 * mounted picker, so opening five image layers does not fire five requests. A
 * mutation in the Brand-Grafiken panel calls invalidateBrandAssets(), which refetches
 * and re-renders every mounted consumer.
 *
 * Fail soft at THIS edge only (value-4): if the fetch fails the editor still works
 * with the curated seed, and `error` lets the caller show a small hint — an upload
 * library outage must never block authoring with the built-in graphics.
 */

// null = not yet loaded; an array (possibly empty) = loaded. Kept at module scope so
// it is shared across every hook instance.
let uploadedCache = null;
let hadError = false;
let inflight = null;
const listeners = new Set();
const notify = () => listeners.forEach(fn => fn());

// First load — guarded so concurrent mounts share one request; a resolved cache is a
// no-op. invalidateBrandAssets() is the explicit refetch path.
const loadOnce = () => {
    if (uploadedCache !== null || inflight) return;
    inflight = getSocialBrandAssets()
        .then(rows => {
            uploadedCache = Array.isArray(rows) ? rows : [];
            hadError = false;
        })
        .catch(() => {
            uploadedCache = [];
            hadError = true;
        })
        .finally(() => {
            inflight = null;
            notify();
        });
};

/**
 * Refetch the uploaded library and re-render every mounted consumer. Called after an
 * upload/delete in the Brand-Grafiken panel. Keeps the current list visible until the
 * refetch resolves (no flicker); on error the last-good list is kept.
 */
export const invalidateBrandAssets = () => {
    inflight = getSocialBrandAssets()
        .then(rows => {
            uploadedCache = Array.isArray(rows) ? rows : [];
            hadError = false;
        })
        .catch(() => {
            hadError = true;
        })
        .finally(() => {
            inflight = null;
            notify();
        });
};

/**
 * @returns {{assets: Array<{src:string,label:string}>, error: boolean}} the merged
 *   picker list (curated first, uploads deduped) and whether the upload fetch failed.
 */
const useBrandAssets = () => {
    const [, setTick] = useState(0);
    useEffect(() => {
        const rerender = () => setTick(t => t + 1);
        listeners.add(rerender);
        loadOnce();
        return () => listeners.delete(rerender);
    }, []);
    return {
        assets: mergeBrandAssets(BRAND_ASSETS, uploadedCache || []),
        error: hadError,
    };
};

export default useBrandAssets;
